import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import PaymentConfigForm from 'src/supportPortal/components/common/paymentConfig/PaymentConfigForm'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getPractitionerOnboardingDetail } from 'src/supportPortal/services/practitionerOnboardingService'

/**
 * Dedicated Payment Configuration page for a practitioner.
 *
 * Phase 5a moved payouts off the inline onboarding flow and onto its own sub-route so the form
 * has the full screen for individual-payee fields (UPI, bank, GST/PAN) and the verification-proof
 * upload. The main onboarding page redirects here after a successful save.
 */
export default function PractitionerPaymentConfigPage() {
  const navigate = useNavigate()
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const practitionerId = params.get('practitioner-id') || ''
  const practitionerSlug = params.get('practitioner-slug') || ''

  const [detailState, setDetailState] = useState({ loading: Boolean(practitionerId || practitionerSlug), error: '', data: null })

  useEffect(() => {
    if (!practitionerId && !practitionerSlug) return
    let active = true
    ;(async () => {
      const response = await getPractitionerOnboardingDetail({ practitionerId, practitionerSlug })
      if (!active) return
      if (response.ok) {
        setDetailState({ loading: false, error: '', data: response.data })
      } else {
        setDetailState({ loading: false, error: response.message || 'Could not load practitioner.', data: null })
      }
    })()
    return () => {
      active = false
    }
  }, [practitionerId, practitionerSlug])

  const practitioner = detailState.data?.practitioner
  const heading = practitioner
    ? `Payment Configuration — ${practitioner.title || practitioner.name || practitioner.id}`
    : 'Payment Configuration'

  if (detailState.loading) {
    return (
      <>
        <SupportHeader heading="Payment Configuration" showBackButton />
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography color="text.secondary">Loading practitioner...</Typography>
          </Stack>
        </Box>
      </>
    )
  }

  if (!practitionerId && !practitionerSlug) {
    return (
      <>
        <SupportHeader heading="Payment Configuration" showBackButton />
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">
            This page expects a <code>practitioner-id</code> query param. Open it from the onboarding flow.
          </Alert>
        </Box>
      </>
    )
  }

  if (detailState.error || !practitioner) {
    return (
      <>
        <SupportHeader heading="Payment Configuration" showBackButton />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{detailState.error || 'Practitioner not found.'}</Alert>
        </Box>
      </>
    )
  }

  return (
    <>
      <SupportHeader heading={heading} showBackButton />
      <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f8fafc', minHeight: '100%' }}>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Configure payouts for an individual practitioner
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use individual-payee fields (PAN, bank account, UPI). When verified, attach a verification-proof document so finance has a record.
                </Typography>
              </Box>
              <Button
                variant="text"
                onClick={() => {
                  const back = new URLSearchParams()
                  back.set('practitioner-id', practitionerId)
                  if (practitionerSlug) back.set('practitioner-slug', practitionerSlug)
                  navigate(`/support-home/practitioner-management/onboarding?${back.toString()}`)
                }}
              >
                Back to onboarding
              </Button>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <PaymentConfigForm entityId={practitioner.id} entityType="mentor" hideEntitySection />
          </Paper>
        </Stack>
      </Box>
    </>
  )
}
